import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogoutIcon2 } from "../assets/icons/Logout";
import { SettingsIcon } from "../assets/icons/SettingIscon";
import UsersIcon from "../assets/icons/UserIscon";
import {
  BANK_ACCOUNTS,
  BUDGET,
  ROLE,
  STORAGE_REFRESH_TOKEN,
  STORAGE_TOKEN,
  STORAGE_USER_DATA,
  SUBSCRIPTION,
  USERNAME,
  NOTIFICATION_SETTINGS
} from "../constants";
import { END_DATE, SELECTED_LABEL, START_DATE } from "../constants/dashboard";
import { redirectToAuth } from "../utils/navigation";
import userPool from "../utils/userPool";
import { UseAuthContext } from "../context/useAuthContext";
import useCognito from "../hooks/auth/useCognito";
import SubscriptionPage from "../pages/Subscription";
import { ViewProfile } from "./settings/profile/View";
import { useCompanyStore } from "../state/useCompanyStore";
import pkg from "../../package.json";
import { useSubscription } from "../context/SubscriptionProvider";
export const appVersion = process.env.REACT_APP_VERSION ?? "dev";

export const handleLogout = () => {
  const defaultSettings = {
    restockAlert: { stock: 5, channels: ["email"], enabled: false },
    marketing: { enabled: false },
    weeklyReport: { email: [] },
  };

  const user = userPool.getCurrentUser();
  if (user) {
    user.signOut();
  } else {
    console.error("No user found in the current session.");
  }

  // Clearing session storage
  localStorage.removeItem(STORAGE_TOKEN);
  localStorage.removeItem(STORAGE_REFRESH_TOKEN);
  localStorage.removeItem(USERNAME);

  // Clearing local storage
  localStorage.removeItem(SUBSCRIPTION);
  localStorage.removeItem(ROLE);
  localStorage.removeItem(STORAGE_USER_DATA);
  localStorage.removeItem("isRootUser");
  localStorage.removeItem("isPasswordChanged");
  localStorage.removeItem(START_DATE);
  localStorage.removeItem(END_DATE);
  localStorage.removeItem(SELECTED_LABEL);
  localStorage.removeItem(BANK_ACCOUNTS);
  localStorage.removeItem(BUDGET);
  localStorage.removeItem(NOTIFICATION_SETTINGS);

  // Removing cookies by iterating over document.cookie
  document.cookie.split(";").forEach((cookie) => {
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  });

  redirectToAuth();
};

//@ts-ignore
const ProfileDropdown = ({ onClose }) => {
  const { user } = useCompanyStore((state) => ({
    user: state.company,
  }));
  const { subscription: subscriptionData } = useSubscription();
  const firstName = user?.firstName || "";
  const lastName = user?.lastName || "";
  const firstInitial = firstName.charAt(0).toUpperCase();
  const lastInitial = lastName.charAt(0).toUpperCase();
  const { logout } = useCognito();
  const [showModal, setShowModal] = useState(false); // State to control modal visibility
  const [showProfileModal, setShowProfileModal] = useState(false); // Profile Modal State

  const navigate = useNavigate();
  const handleSettings = () => {
    navigate("/settings");
    onClose();
  };

  const handleProfile = () => {
    setShowProfileModal(true); // Show modal instead of navigating
    onClose(); // Close the dropdown
  };

  const handleSubscription = () => {
    navigate("/subscription");
    onClose();
  };

  return (
    <div className="relative lg:w-[350px] bg-white  shadow-lg rounded-[20px]  border z-9999">
      <div className="p-4">
        {/* User Info */}
        <div className="flex items-center space-x-4 lg:text-[20px] ">
          <div className="w-14 h-14 bg-[#FEE4E2] text-[#B42318] rounded-full flex items-center justify-center font-medium p-5">
            {firstInitial + lastInitial}
          </div>
          <div>
            <p className="lg:text-[16px] text-sm font-semibold text-gray-800 mb-2">
              {user.firstName} {user.lastName}
            </p>
            <p className="lg:text-[16px] text-sm  text-gray-500">
              {user.email}
            </p>
          </div>
        </div>

        {/* Options */}
        <div className="mt-4 space-y-4 lg:text-[16px] text-sm  border-t border-gray-200 pt-4">
          <div
            className="flex items-center lg:text-[16px] text-sm  space-x-2 text-gray-700 hover:text-blue-600 cursor-pointer"
            onClick={handleSettings}
          >
            <SettingsIcon />
            <span>Settings</span>
          </div>

          <div
            className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 cursor-pointer"
            onClick={() => setShowProfileModal(true)}
          >
            <UsersIcon />
            <span>My accounts</span>
          </div>
        </div>

        {/* Plan Details */}
        <div className="mt-4 border-t border-gray-200 pt-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="lg:text-[16px] text-sm  font-semibold text-gray-800 capitalize">
                {subscriptionData?.plan}
              </p>

              <span className="lg:text-[14px] text-sm  text-gray-700">
                {subscriptionData?.plan === "free"
                  ? "Upgrade for more invoices"
                  : `Create ${subscriptionData?.limits?.invoices || 0}+ invoices`}
              </span>
            </div>
            <button
              className="lg:text-[14px] text-sm  text-blue-600 border border-blue-600 rounded-md px-3 py-1  hover:bg-blue-100 whitespace-nowrap"
              onClick={() => {
                if (subscriptionData?.plan === "free") {
                  setShowModal(true);
                } else {
                  navigate("/settings?tab=subscription");
                }
              }}
            >
              {subscriptionData?.plan === "free" ? "Upgrade plan" : "View plan"}
            </button>
          </div>
        </div>

        {/* Logout */}
        <div
          className="mt-4 border-t border-gray-200 pt-4 lg:text-[16px] text-sm "
          onClick={handleLogout}
        >
          <div className="flex items-center space-x-2 text-red-600 hover:text-red-800 cursor-pointer">
            <LogoutIcon2 />
            <span>Log out</span>
          </div>
        </div>
      </div>
      <div className="bg-[#F9FAFB] text-[#667085] text-sm font-medium p-2.5 flex w-full justify-center items-center rounded-b-[20px]">
        App version v.{pkg?.version || appVersion}
      </div>

      {/* Render the SubscriptionModal */}
      {showModal && (
        <SubscriptionPage
          close={() => setShowModal(false)} // Close modal
        />
      )}

      <>
        {/* Render the ViewProfile modal */}
        {showProfileModal && (
          <ViewProfile
            onClose={() => setShowProfileModal(false)} // Close modal
          />
        )}
      </>
    </div>
  );
};

export default ProfileDropdown;
